import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { GetScanResultResponse } from "pages/draft-overview/definitions";
import {
  formatTimestamp,
  processFieldValue,
  processGovernmentEntityValue,
} from "components/create-edit-gaiaobject-form/util";

export interface FieldMappingConfig {
  country: string;
  governmentEntity: string;
  localJurisdiction: string;
  entity: string;
  taxType: string;
  taxAuthority: string;
  auditStartDate: string;
  auditEndDate: string;
  auditInitiationDate: string;
  auditCloseDate: string;
  statuteOfLimitations: string;
  description: string;
  auditNumber: string;
  taxNumber: string;
  auditNameSuffix: string;
  owner: string;
  customAttributes?: {
    [key: string]: string;
  };
  [key: string]: string | { [key: string]: string } | undefined;
}

export const AUDIT_BASE_FIELD_MAPPING = {
  country: "country",
  governmentEntity: "governmentEntity",
  localJurisdiction: "localJurisdiction",
  entity: "entity",
  taxType: "taxType",
  taxAuthority: "taxAuthority",
  auditStartDate: "auditPeriodStart",
  auditEndDate: "auditPeriodEnd",
  auditInitiationDate: "auditInitiationDate",
  auditCloseDate: "auditCloseDate",
  statuteOfLimitations: "statuteOfLimitations",
  description: "description",
  auditNumber: "auditNumber",
  taxNumber: "taxNumber",
  auditNameSuffix: "nameSuffix",
  owner: "auditOwner",
};

export const GAIA_DEV_FIELD_MAPPING: FieldMappingConfig = {
  ...AUDIT_BASE_FIELD_MAPPING,
  noticeNumber: "noticeNumber",
  noticeName: "noticeName",
};

export const US_SALT_DIRECT_FIELD_MAPPING: FieldMappingConfig = {
  ...AUDIT_BASE_FIELD_MAPPING,
  customAttributes: {
    plannedStartDate: "plannedStartDate",
    plannedEndDate: "plannedEndDate",
  },
};

export const US_SALT_INDIRECT_FIELD_MAPPING: FieldMappingConfig = {
  ...AUDIT_BASE_FIELD_MAPPING,
  customAttributes: {
    plannedStartDate: "plannedStartDate",
    plannedEndDate: "plannedEndDate",
  },
};

export const US_PROPERTY_TAX_FIELD_MAPPING: FieldMappingConfig = {
  ...AUDIT_BASE_FIELD_MAPPING,
  taxNumber: "accountNumber",
  customAttributes: {
    auditNoticeDate: "auditNoticeDate",
    nextActionDate: "nextActionDate",
  },
};

export const TEAM_FIELD_MAPPINGS: Record<string, FieldMappingConfig> = {
  gaia_dev: GAIA_DEV_FIELD_MAPPING,
  us_salt_direct: US_SALT_DIRECT_FIELD_MAPPING,
  us_salt_indirect: US_SALT_INDIRECT_FIELD_MAPPING,
  us_property_tax: US_PROPERTY_TAX_FIELD_MAPPING,
};

function getFieldValue(scanResult: any, primaryField: string | undefined): any {
  if (!primaryField) {
    return undefined;
  }

  if (
    scanResult[primaryField] !== undefined &&
    scanResult[primaryField] !== null
  ) {
    return scanResult[primaryField];
  }

  return undefined;
}

// Generic transform function that uses team-specific field mappings
export function transformScanResultToAuditFormDataWithMapping(
  scanResult: GetScanResultResponse["scanResult"],
  teamName: string,
  customAttributes?: any[],
  uiContent?: any,
  scanJobCreatedBy?: string,
) {
  const auditScanResult = scanResult as any;

  // Get field mapping for the team, fallback to gaia_dev if not found
  const fieldMapping = TEAM_FIELD_MAPPINGS[teamName] || GAIA_DEV_FIELD_MAPPING;

  const customAttributesData: any = {};
  if (fieldMapping.customAttributes) {
    Object.entries(fieldMapping.customAttributes).forEach(
      ([attrName, fieldName]) => {
        const scanValue = getFieldValue(auditScanResult, fieldName);
        if (scanValue !== undefined && scanValue !== null) {
          if (attrName.toLowerCase().includes("date")) {
            customAttributesData[attrName] = formatTimestamp(scanValue);
          } else {
            customAttributesData[attrName] = scanValue;
          }
        }
      },
    );
  }

  if (customAttributes && customAttributes.length > 0) {
    customAttributes.forEach((attr) => {
      const fieldName = attr.attributeName;
      if (fieldMapping.customAttributes?.[fieldName]) {
        return;
      }

      let scanValue = auditScanResult[fieldName];

      if (scanValue === undefined || scanValue === null) {
        const snakeCaseField = fieldName
          .replace(/([A-Z])/g, "_$1")
          .toLowerCase();
        scanValue = auditScanResult[snakeCaseField];
      }

      if (scanValue === undefined || scanValue === null) {
        scanValue = auditScanResult[fieldName.toLowerCase()];
      }

      if (scanValue !== undefined && scanValue !== null) {
        customAttributesData[fieldName] = processFieldValue(
          fieldName,
          scanValue,
          customAttributes,
          uiContent,
        );
      }
    });
  }

  const taxTypeValue = getFieldValue(auditScanResult, fieldMapping.taxType);

  const entityValue = getFieldValue(auditScanResult, fieldMapping.entity);

  const countryValue = getFieldValue(auditScanResult, fieldMapping.country);
  const governmentEntityValue = getFieldValue(
    auditScanResult,
    fieldMapping.governmentEntity,
  );

  const processedGovernmentEntity = governmentEntityValue
    ? processGovernmentEntityValue(
        countryValue,
        governmentEntityValue,
        uiContent,
      )
    : governmentEntityValue;

  return {
    country: countryValue,
    governmentEntity: processedGovernmentEntity,
    localJurisdiction: getFieldValue(
      auditScanResult,
      fieldMapping.localJurisdiction,
    ),
    entity: entityValue,
    taxType: taxTypeValue
      ? Array.isArray(taxTypeValue)
        ? taxTypeValue
        : [taxTypeValue]
      : [],
    taxAuthority: getFieldValue(auditScanResult, fieldMapping.taxAuthority),
    auditStartDateString: formatTimestamp(
      getFieldValue(auditScanResult, fieldMapping.auditStartDate),
    ),
    auditEndDateString: formatTimestamp(
      getFieldValue(auditScanResult, fieldMapping.auditEndDate),
    ),
    auditInitiationDateString: formatTimestamp(
      getFieldValue(auditScanResult, fieldMapping.auditInitiationDate),
    ),
    auditCloseDateString: (() => {
      const closeDate = getFieldValue(
        auditScanResult,
        fieldMapping.auditCloseDate,
      );
      return closeDate && closeDate !== "1970-01-01T00:00:00Z"
        ? formatTimestamp(closeDate)
        : undefined;
    })(),
    statuteOfLimitationsString: formatTimestamp(
      getFieldValue(auditScanResult, fieldMapping.statuteOfLimitations),
    ),
    description: getFieldValue(auditScanResult, fieldMapping.description),
    auditNumber: getFieldValue(auditScanResult, fieldMapping.auditNumber),
    taxNumber: getFieldValue(auditScanResult, fieldMapping.taxNumber),
    auditNameSuffix: getFieldValue(
      auditScanResult,
      fieldMapping.auditNameSuffix,
    ),
    owner:
      scanJobCreatedBy || getFieldValue(auditScanResult, fieldMapping.owner),
    noticeNumber: getFieldValue(
      auditScanResult,
      fieldMapping.noticeNumber as string,
    ),
    noticeName: getFieldValue(
      auditScanResult,
      fieldMapping.noticeName as string,
    ),
    [CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME]: customAttributesData,
  };
}
